import React, { useEffect, useRef, useState } from "react";
import { Box, Container, Icon, Typography, keyframes } from "@mui/material";
import { useTheme } from "@emotion/react";
import WaitlistCTA from "./WaitlistCTA.js";

/**
 * Concentric ring pulse for the Skill badge. Slow, subtle expand-and-fade
 * so the AI moment feels alive without stealing the eye from the copy.
 */
const skillRingPulse = keyframes`
  0%   { transform: scale(0.95); opacity: 0.35; }
  70%  { transform: scale(1.55); opacity: 0;    }
  100% { transform: scale(1.55); opacity: 0;    }
`;

/**
 * Small inline brand marks for Google Drive + OneDrive, shown next to the
 * "Delivered to your Drive" label so it reads as "lands in the tools
 * teachers already use" rather than a Maestro-specific inbox.
 */
const GoogleDriveMark = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 87.3 78"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3L27.5 53H0c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
    <path d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L1.2 48.4C.4 49.8 0 51.35 0 52.9h27.5z" fill="#00ac47" />
    <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.75l5.85 11.5z" fill="#ea4335" />
    <path d="M43.65 25L57.4 1.2C56.05.4 54.5 0 52.9 0H34.4c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
    <path d="M59.75 53H27.55L13.85 76.8c1.35.8 2.9 1.2 4.5 1.2h50.6c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
    <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.1 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
  </svg>
);

const OneDriveMark = () => (
  <svg
    width="18"
    height="14"
    viewBox="0 0 24 16"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path d="M13.9 5.2c-.6-1.7-2.2-2.9-4.1-2.9-1.9 0-3.5 1.2-4.1 2.9-2 .3-3.5 2-3.5 4 0 2.2 1.8 4 4 4h11c1.7 0 3-1.4 3-3 0-1.3-.8-2.4-2-2.8-.7-1.4-2.1-2.4-3.8-2.4-.2 0-.3 0-.5.2z" fill="#0364B8" />
    <path d="M18.9 8.9c-.4 0-.9.1-1.3.3.1.4.2.8.2 1.2 0 1.6-1.3 3-3 3H10.5c.4 1.5 1.8 2.6 3.5 2.6h5c2.1 0 3.8-1.7 3.8-3.8s-1.7-3.7-3.9-3.3z" fill="#1490DF" />
  </svg>
);

const DriveBadges = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      opacity: 0.85,
    }}
    aria-label="Google Drive and OneDrive"
    title="Google Drive · OneDrive"
  >
    <GoogleDriveMark />
    <OneDriveMark />
  </Box>
);

/**
 * Featured output tiles rendered inside the workflow diagram. Icons +
 * gradient chip backgrounds mirror the materials grid below so the reader
 * sees consistent visual language between the diagram payoff and the grid.
 */
const outputTiles = [
  {
    icon: "layers",
    gradient: "linear-gradient(135deg, #fbbf24, #d97706)",
    name: "Scaffolded Worksheets",
    sub: "Three tiers",
  },
  {
    icon: "grid_view",
    gradient: "linear-gradient(135deg, #f472b6, #db2777)",
    name: "Gallery Walks",
    sub: "Interactive stations",
  },
  {
    icon: "menu_book",
    gradient: "linear-gradient(135deg, #60a5fa, #2563eb)",
    name: "Study Guides",
    sub: "Standards-aligned",
  },
  {
    icon: "replay",
    gradient: "linear-gradient(135deg, #c084fc, #7c3aed)",
    name: "Reteach Materials",
    sub: "Data-driven",
  },
];

/**
 * Homepage material tiles. `href` points to a specific example artifact in
 * Drive so a curious visitor can open a real sample, not a folder. File IDs
 * mirror `apps/server/prompts/data/growth_full.html` (the one-pager) so
 * updating the source-of-truth there is enough for both surfaces.
 */
const materials = [
  {
    icon: "psychology",
    color: "success",
    name: "CFUs & Misconceptions",
    desc: "Targeted checks-for-understanding + reteach angles for the specific misconceptions on last week's assessment.",
    href: "https://drive.google.com/file/d/1VzX4vmgsZMSx1ErBy2wzeY5KnfvTupPS/view?usp=drive_link",
  },
  {
    icon: "layers",
    color: "warning",
    name: "Scaffolded Worksheets",
    desc: "Same standard, three tiers. Color-coded scaffolds for students working 2-3 grade levels below.",
    href: "https://drive.google.com/file/d/1YLKZzmtPfcujUB3dAqMKnbrx-XNNr32z/view?usp=drive_link",
  },
  {
    icon: "menu_book",
    color: "info",
    name: "Study Guides",
    desc: "Detailed, standard-aligned review. Students love them; teachers don't have to build them from scratch.",
    href: "https://drive.google.com/file/d/1Z4QE_Gh4kOmFU4DlBKbvmBhArkpUKHZW/view?usp=drive_link",
  },
  {
    icon: "grid_view",
    color: "gold",
    name: "Gallery Walks",
    desc: "6-8 stations, 5-7 minutes each. Interactive scaffolding for units where students learn by moving.",
    href: "https://drive.google.com/file/d/1TSf3pK2r2N2NpYOQ817QC_foRRxAfjJc/view?usp=drive_link",
  },
  {
    icon: "science",
    color: "dark",
    name: "Take-Home Labs",
    desc: "Home-friendly experiments and data-collection kits that turn science into a family conversation.",
    href: "https://drive.google.com/file/d/1AfKKOe60rEx3vo41KQpzDYd8FG_cRVTF/view?usp=drive_link",
  },
  {
    icon: "replay",
    color: "purple",
    name: "Reteach Materials",
    desc: "The specific reteach a specific student needs, based on last week's exit ticket data.",
    href: "https://drive.google.com/file/d/1tPf0e5Vq_PhRvgNYkryiY17fuXlZVJ5Q/view?usp=drive_link",
  },
];

const iconColorMap = {
  success: "#209C33",
  warning: "#FF7F07",
  info: "#2563eb",
  gold: "#eab308",
  dark: "#14532d",
  purple: "#6b21a8",
};

/**
 * Split an array into fixed-size chunks. Used to lay the 6 materials into
 * two mobile carousel pages of 3 tiles each.
 */
const chunk = (arr, size) =>
  arr.reduce((acc, _, i) => (i % size ? acc : [...acc, arr.slice(i, i + size)]), []);

const HowItWorks = () => {
  const theme = useTheme();
  const diagramRef = useRef(null);
  const [played, setPlayed] = useState(false);
  const materialsScrollRef = useRef(null);
  const [materialsPage, setMaterialsPage] = useState(0);
  const materialsPages = chunk(materials, 3);

  /**
   * Track which page is centered in the horizontal snap carousel so the
   * dot pagination reflects the active column of 3 tiles.
   */
  useEffect(() => {
    const el = materialsScrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const pageWidth = el.firstChild?.offsetWidth || 1;
      const gap = 16;
      const idx = Math.round(el.scrollLeft / (pageWidth + gap));
      setMaterialsPage(Math.min(Math.max(idx, 0), materialsPages.length - 1));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [materialsPages.length]);

  const scrollToMaterialsPage = (idx) => {
    const el = materialsScrollRef.current;
    if (!el) return;
    const pageWidth = el.firstChild?.offsetWidth || 0;
    const gap = 16;
    el.scrollTo({ left: (pageWidth + gap) * idx, behavior: "smooth" });
  };

  useEffect(() => {
    if (!diagramRef.current || played) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setPlayed(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.35 }
    );
    observer.observe(diagramRef.current);
    return () => observer.disconnect();
  }, [played]);

  const isActive = (idx) => played;
  const activeStyle = (idx) => ({
    opacity: isActive(idx) ? 1 : 0,
    transform: isActive(idx) ? "translateY(0)" : "translateY(20px)",
    transition: `opacity 0.5s ease ${idx * 0.18}s, transform 0.5s ease ${
      idx * 0.18
    }s`,
  });

  return (
    <Box
      component="section"
      id="how"
      sx={{ py: { xs: 8, md: 14 }, bgcolor: "#f7f8fa" }}
    >
      <Container maxWidth="lg">
        <Typography
          sx={{
            display: "inline-block",
            fontSize: 12,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: theme.palette.success.main,
            fontWeight: 700,
            mb: 2.5,
          }}
        >
          How it works
        </Typography>
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: "2rem", md: "3.25rem" },
            fontWeight: 800,
            letterSpacing: "-0.03em",
            lineHeight: 1.06,
            color: theme.palette.text.primary,
          }}
        >
          Map{" "}
          <Box component="span" sx={{ color: theme.palette.success.main }}>
            your workflows
          </Box>
          . We build them into your AI skill.
        </Typography>
        <Typography
          sx={{
            fontSize: "1.25rem",
            color: theme.palette.text.secondary,
            mt: 2.75,
            maxWidth: 640,
            lineHeight: 1.5,
          }}
        >
          Most AI products give every user the same behavior. Not us. Every
          Maestro teacher gets a personalized AI skill that knows their
          curriculum, their students, and the workflows they already run.
        </Typography>

        {/* Workflow diagram — V4: vertical stack on mobile, horizontal 3-column on desktop */}
        <Box
          ref={diagramRef}
          sx={{
            mt: 8,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "stretch" },
            gap: { xs: 1, md: 2 },
            maxWidth: { xs: 780, md: "100%" },
            mx: "auto",
          }}
        >
          {/* Inputs card — header + two tinted rows */}
          <Box
            sx={{
              bgcolor: theme.palette.paper.primary,
              borderRadius: 4,
              p: 1,
              boxShadow:
                "0 20px 40px -12px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)",
              flex: { md: 1 },
              display: "flex",
              flexDirection: "column",
              ...activeStyle(0),
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: { xs: 1.5, md: 2 },
                pt: 1.25,
                pb: 0.75,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  color: theme.palette.text.secondary,
                }}
              >
                Inputs
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 10,
                  fontWeight: 700,
                  color: theme.palette.success.main,
                  bgcolor: "#f0fdf4",
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 999,
                  letterSpacing: "0.1em",
                }}
              >
                STEP 01
              </Typography>
            </Box>

            {[
              {
                icon: "person",
                grad: "linear-gradient(135deg, #22c55e, #15803d)",
                wash:
                  "linear-gradient(90deg, #f0fdf4, rgba(240,253,244,0) 60%)",
                title: "Your context",
                sub: "Curriculum, students, pacing",
              },
              {
                icon: "insights",
                grad: "linear-gradient(135deg, #3b82f6, #1e40af)",
                wash:
                  "linear-gradient(90deg, #eff6ff, rgba(239,246,255,0) 60%)",
                title: "Your data",
                sub: "MAP, i-Ready, exit tickets, IEPs",
              },
            ].map((row) => (
              <Box
                key={row.title}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.75,
                  px: { xs: 1.75, md: 2.25 },
                  py: { xs: 1.75, md: 2 },
                  m: 0.25,
                  borderRadius: 3,
                  background: row.wash,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: row.grad,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
                    flexShrink: 0,
                  }}
                >
                  <Icon
                    baseClassName="material-symbols-outlined"
                    sx={{ fontSize: 22, color: "#fff" }}
                  >
                    {row.icon}
                  </Icon>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: "0.9375rem", md: "1rem" },
                      fontWeight: 800,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {row.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.8125rem",
                      color: theme.palette.text.secondary,
                      mt: 0.25,
                    }}
                  >
                    {row.sub}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: theme.palette.success.main,
              py: { xs: 0.5, md: 0 },
              px: { xs: 0, md: 0.5 },
              flexShrink: 0,
            }}
          >
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{ fontSize: 26, display: { xs: "block", md: "none" } }}
            >
              south
            </Icon>
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{ fontSize: 28, display: { xs: "none", md: "block" } }}
            >
              arrow_forward
            </Icon>
          </Box>

          {/* Skill card — dark navy w/ radial green glow */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
              color: "#fff",
              borderRadius: 4,
              p: { xs: 3, md: 4 },
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 20px 40px -12px rgba(15,23,42,0.35)",
              flex: { md: 1 },
              display: "flex",
              flexDirection: "column",
              ...activeStyle(2),
              "&::before": {
                content: '""',
                position: "absolute",
                top: -60,
                right: -60,
                width: 240,
                height: 240,
                background:
                  "radial-gradient(circle, rgba(34,197,94,0.4) 0%, transparent 60%)",
                pointerEvents: "none",
              },
            }}
          >
            {/* Header row — pinned to top, aligns with STEP 01 / STEP 03 */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                position: "relative",
                zIndex: 1,
                mt: {md: -2},
                mb: {md: 2}
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "rgba(74,222,128,0.9)",
                  fontWeight: 700,
                }}
              >
                AI Skill
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#4ade80",
                  bgcolor: "rgba(34,197,94,0.15)",
                  border: "1px solid rgba(34,197,94,0.35)",
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 999,
                  letterSpacing: "0.1em",
                }}
              >
                STEP 02
              </Typography>
            </Box>

            {/* Content — top-aligned so hero title lines up with "Your context" and outputs grid */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                position: "relative",
                zIndex: 1,
                mt: { xs: 2.5, md: 3 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {/* Skill badge — circular, larger, with slow subtle ring pulse to signal the AI moment */}
                <Box
                  sx={{
                    position: "relative",
                    width: 60,
                    height: 60,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {/* Slow, subtle pulse rings */}
                  {[0, 2.5].map((delay) => (
                    <Box
                      key={delay}
                      aria-hidden
                      sx={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: "50%",
                        border: "1.5px solid rgba(74, 222, 128, 0.5)",
                        animation: `${skillRingPulse} 5s ease-out infinite`,
                        animationDelay: `${delay}s`,
                        pointerEvents: "none",
                      }}
                    />
                  ))}
                  {/* Core badge — smooth diagonal gradient */}
                  <Box
                    sx={{
                      position: "relative",
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      background:
                        "linear-gradient(135deg, #4ade80 0%, #22c55e 55%, #15803d 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow:
                        "0 0 28px rgba(34,197,94,0.35), 0 4px 16px rgba(34,197,94,0.3)",
                    }}
                  >
                    <Icon
                      baseClassName="material-symbols-outlined"
                      sx={{ fontSize: 30, color: "#fff" }}
                    >
                      auto_awesome
                    </Icon>
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: "1.25rem", md: "1.5rem" },
                    fontWeight: 800,
                    letterSpacing: "-0.02em",
                    lineHeight: 1.2,
                  }}
                >
                  Personalized to you
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: "0.875rem", md: "0.9375rem" },
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: 1.5,
                  mt: 2,
                }}
              >
                Refined weekly with your coach, with no shared prompts and no
                generic behavior.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: theme.palette.success.main,
              py: { xs: 0.5, md: 0 },
              px: { xs: 0, md: 0.5 },
              flexShrink: 0,
            }}
          >
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{ fontSize: 26, display: { xs: "block", md: "none" } }}
            >
              south
            </Icon>
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{ fontSize: 28, display: { xs: "none", md: "block" } }}
            >
              arrow_forward
            </Icon>
          </Box>

          {/* Outputs column — header + 2x2 grid */}
          <Box sx={{ flex: { md: 1.2 }, display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 0.5,
                mb: 1,
                ...activeStyle(3),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    color: theme.palette.text.secondary,
                  }}
                >
                  Delivered to your Drive
                </Typography>
                <DriveBadges />
              </Box>
              <Typography
                sx={{
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 10,
                  fontWeight: 700,
                  color: theme.palette.success.main,
                  bgcolor: "#f0fdf4",
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 999,
                  letterSpacing: "0.1em",
                }}
              >
                STEP 03
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1.25,
                flex: 1,
              }}
            >
              {outputTiles.map((tile, i) => (
              <Box
                key={tile.name}
                sx={{
                  bgcolor: theme.palette.paper.primary,
                  borderRadius: 3,
                  p: 2,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  transition: "transform 0.15s, box-shadow 0.15s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                  },
                  ...activeStyle(4 + i),
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    background: tile.gradient,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1.25,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  }}
                >
                  <Icon
                    baseClassName="material-symbols-outlined"
                    sx={{ fontSize: 20, color: "#fff" }}
                  >
                    {tile.icon}
                  </Icon>
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: 800,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.3,
                  }}
                >
                  {tile.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.6875rem",
                    color: theme.palette.text.quaternary,
                    mt: 0.5,
                  }}
                >
                  {tile.sub}
                </Typography>
              </Box>
            ))}
            </Box>
          </Box>
        </Box>

        {/* Materials grid */}
        <Box sx={{ mt: 7 }}>
          <Typography
            sx={{
              fontSize: "1.375rem",
              fontWeight: 700,
              letterSpacing: "-0.01em",
              mb: 3,
            }}
          >
            Examples of what teachers get.
          </Typography>
          {/* Desktop: 3-column grid; Mobile: horizontal snap-scroll with 2 pages of 3 stacked tiles */}
          {(() => {
            const renderTile = (m) => (
              <Box
                key={m.name}
                component="a"
                href={m.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  bgcolor: theme.palette.paper.primary,
                  border: `1px solid ${theme.palette.text.tertiary}`,
                  borderRadius: 2,
                  p: 3,
                  display: "block",
                  textDecoration: "none",
                  color: "inherit",
                  transition: "border-color 0.15s, transform 0.15s",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: theme.palette.success.main,
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.25,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    bgcolor: iconColorMap[m.color],
                    mb: 2,
                  }}
                >
                  <Icon
                    baseClassName="material-symbols-outlined"
                    sx={{ fontSize: 22 }}
                  >
                    {m.icon}
                  </Icon>
                </Box>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                  }}
                >
                  {m.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.84375rem",
                    color: theme.palette.text.secondary,
                    mt: 0.75,
                    lineHeight: 1.5,
                  }}
                >
                  {m.desc}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: theme.palette.success.main,
                    fontWeight: 600,
                    mt: 1.75,
                  }}
                >
                  View sample →
                </Typography>
              </Box>
            );

            return (
              <>
                {/* Desktop grid */}
                <Box
                  sx={{
                    display: { xs: "none", md: "grid" },
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 2,
                  }}
                >
                  {materials.map(renderTile)}
                </Box>

                {/* Mobile carousel — each snap page holds a column of 3 tiles */}
                <Box sx={{ display: { xs: "block", md: "none" } }}>
                  <Box
                    ref={materialsScrollRef}
                    sx={{
                      display: "flex",
                      gap: 2,
                      overflowX: "auto",
                      scrollSnapType: "x mandatory",
                      WebkitOverflowScrolling: "touch",
                      mx: -3,
                      px: 3,
                      pb: 1,
                      "&::-webkit-scrollbar": { display: "none" },
                      scrollbarWidth: "none",
                    }}
                  >
                    {materialsPages.map((page, pageIdx) => (
                      <Box
                        key={pageIdx}
                        sx={{
                          flex: "0 0 88%",
                          scrollSnapAlign: "center",
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {page.map(renderTile)}
                      </Box>
                    ))}
                  </Box>

                  {/* Dot pagination — 1 dot per page of 3 tiles */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 1,
                      mt: 2.5,
                    }}
                  >
                    {materialsPages.map((_, i) => (
                      <Box
                        key={i}
                        component="button"
                        aria-label={`Go to page ${i + 1}`}
                        onClick={() => scrollToMaterialsPage(i)}
                        sx={{
                          width: materialsPage === i ? 24 : 8,
                          height: 8,
                          borderRadius: 999,
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          bgcolor:
                            materialsPage === i
                              ? theme.palette.success.main
                              : theme.palette.text.tertiary,
                          transition: "width 0.2s, background-color 0.2s",
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </>
            );
          })()}
        </Box>

        {/* Delivered-to-Drive banner — icon inline with title, subtitle beneath */}
        <Box
          sx={{
            mt: { xs: 6, md: 8 },
            py: { xs: 4, md: 5 },
            px: { xs: 3, md: 5 },
            bgcolor: "#fafafa",
            border: `1px solid ${theme.palette.text.tertiary}`,
            borderRadius: 3,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "1.5rem", md: "2.25rem" },
              fontWeight: 800,
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
              color: theme.palette.text.primary,
              display: "inline-flex",
              alignItems: "center",
              gap: { xs: 1.25, md: 1.75 },
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{
                fontSize: { xs: 32, md: 44 },
                color: theme.palette.success.main,
              }}
            >
              cloud_done
            </Icon>
            <Box component="span">
              Delivered to your{" "}
              <Box component="span" sx={{ color: theme.palette.success.main }}>
                Drive
              </Box>
              . No new app.
            </Box>
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "0.9375rem", md: "1rem" },
              color: theme.palette.text.secondary,
              mt: 1.25,
              lineHeight: 1.5,
              maxWidth: 620,
              mx: "auto",
            }}
          >
            Everything Maestro creates lands where your teachers already work.
            No new login, no new tool to learn.
          </Typography>
        </Box>

        <WaitlistCTA />
      </Container>
    </Box>
  );
};

export default HowItWorks;
